import React, { useEffect, useState } from "react";
import {
    Badge, Button, Checkbox, CircularProgress, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useTheme
} from "@mui/material";
import { connect } from "react-redux";
//--------------------------|| 3RD PARTY LIBRARIES ||------------------------------------//
import {
    IconCheck,
    IconEdit,
    IconFilter,
    IconPlaylistAdd,
    IconSortAscending2,
    IconSortDescending2,
    IconX
} from "@tabler/icons-react";
//--------------------------|| FILES IMPORT ||------------------------------------------//
import { getPager } from "helper/common";
import MainCard from "ui-component/cards/MainCard";
import SubCard from "ui-component/cards/SubCard";
import { NoData } from "ui-component/custom-components/nodata";
import { VWPagination } from "ui-component/custom-components/pagination";
import {
    clearRegionList,
    closeRegionFilterDialog,
    closeRegionCreateDialog,
    getRegionList,
    openRegionFilterDialog,
    openRegionCreateDialog,
    setRegionFilteredData,
    regionListErrorClear,
    regionEdit,
    regionEditErrorClear
} from "store/app/form-management/region-configuration/actions";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import Chip from "ui-component/extended/Chip";
import moment from "moment";
import CreateRegion from "./create";
import FilterRegion from "./filter";
import { AllowedSpecialCharacters, AllowedSpecialCharactersPaste } from "helper/regex";

const tableHeaderStyle = { paddingLeft: '0px' }

const RegionList = (props) => {
    //--------------------List--------------------------------------
    const { regionList, totalCounts, isPending, errorMessage } = props;

    const [pageSize, setPageSize] = useState(10);
    const [Pager, setPager] = useState({ currentPage: 1 });
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortOrder, setSortOrder] = useState(null);

    //--------------------Filter------------------------------------
    const { regionFilterData, isRegionFilterDialogOpen } = props;
    //--------------------Create------------------------------------
    const { isRegionCreateDialogOpen } = props;
    //--------------------Edit--------------------------------------
    const { isRegionEditPending, regionEditErrorMessage } = props;

    const [isEdit, setIsEdit] = useState();
    const [updatedRegion, setUpdatedRegion] = useState();
    const [errors, setErrors] = useState();

    //--------------------Profile-----------------------------------
    const { userProfile } = props;
    const { userRoleName } = userProfile || {};

    //--------------------UI Design----------------------------------
    const theme = useTheme();
    const colorPrimary = theme.palette.secondary.dark;
    const [tableHeight, setTableHeight] = useState(window.innerHeight);

    //---------------------------------------------------------------------------------------//
    useEffect(() => {
        props.regionListErrorClear();
        props.regionEditErrorClear();

        const handleResize = () => {
            setTableHeight(window.innerHeight)
        };
        window.addEventListener("resize", handleResize);

        return () => {
            props.clearRegionList();
            window.removeEventListener("resize", handleResize);
        }
    }, [])

    //--------------------------|| SORTING ||------------------------------------------//
    const handleSort = (column) => {
        setIsEdit();
        if (sortOrder === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortOrder(column);
            setSortDirection('asc');
        }
    };

    //--------------------------|| PAGINATION ||------------------------------------------//
    const setPage = (page = 1) => {
        setIsEdit();
        props.getRegionList(page, pageSize, sortOrder, sortDirection, regionFilterData);

        setPager(prevPager => {
            const totalPages = Math.ceil(totalCounts / pageSize);
            const visiblePages = 4;
            const pageOffset = Math.max(0, Math.floor((page - 1) / visiblePages) * visiblePages);
            const newPages = [];
            for (let i = pageOffset + 1; i <= Math.min(totalPages, pageOffset + visiblePages); i++) {
                newPages.push(i);
            }
            return {
                ...prevPager,
                currentPage: page,
                totalPages,
                pages: newPages,
            }
        })
    }

    useEffect(() => {
        const currentPager = getPager(totalCounts, 1, pageSize);
        setPager(currentPager)
    }, [totalCounts]);

    useEffect(() => {
        setPage();
    }, [regionFilterData, sortOrder, sortDirection, pageSize])


    //--------------------------|| FILTER ||------------------------------------------//
    const handleFilterOpen = () => {
        setIsEdit();
        props.openRegionFilterDialog();
    }
    const handleFilterClose = () => {
        props.closeRegionFilterDialog();
    }
    //--------------------------|| CREATE ||------------------------------------------//
    const handleRegionCreateOpen = () => {
        setIsEdit();
        props.openRegionCreateDialog();
    }
    const handleRegionCreateClose = () => {
        props.closeRegionCreateDialog();
    }

    //--------------------------|| EDIT ||------------------------------------------//
    const validateOnchange = (e) => {
        const { name, value, type, checked } = e.target;
        setUpdatedRegion({
            ...updatedRegion,
            [name]: type === "checkbox" ? checked : value,
        })
        setErrors({
            ...errors,
            [name]: null,
        })
    }

    const handleUpdateRegion = (e) => {
        e.preventDefault();

        const { regionName } = updatedRegion || {};
        const currentErrors = {};

        if (!regionName && !regionName?.trim()) {
            currentErrors.regionName = 'Region name is required';
        }

        setErrors(currentErrors);

        if (Object.keys(currentErrors).length === 0) {
            props.regionEdit(updatedRegion, Pager && Pager.currentPage, pageSize, sortOrder, sortDirection, regionFilterData)
                .then((result) => {
                    if (result) {
                        setIsEdit();
                        setUpdatedRegion();
                        setErrors();
                    }
                })
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleUpdateRegion(e);
        }
        else {
            AllowedSpecialCharacters(e);
        }
    }

    return (
        <>
            {/*----------------------------|| HEADER ||--------------------------------*/}
            <SubCard sx={{
                p: 1.5,
                pl: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
            >
                <Grid item sx={{ display: 'flex', gap: { lg: 2, md: 2, sm: 1, xs: 1 }, alignItems: "center", marginRight: "10px" }}>
                    <Typography variant="h4">Region List - </Typography>
                    <Tooltip title="Region Count" placement="top">
                        <span>
                            <Chip
                                label={totalCounts}
                                chipcolor="secondary"
                                size="small"
                                sx={{
                                    borderRadius: "20%",
                                    py: 1,
                                    fontWeight: "bold",
                                    fontSize: 15,
                                    cursor: "pointer"
                                }}
                            />
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                    <Badge
                        badgeContent={regionFilterData ? Object.keys(regionFilterData).length : 0}
                        color="secondary"
                        invisible={regionFilterData ? Object.keys(regionFilterData).length === 0 : true}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleFilterOpen}
                            startIcon={<IconFilter />}
                        >
                            Filter
                        </Button>
                    </Badge>
                    {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleRegionCreateOpen}
                            startIcon={<IconPlaylistAdd />}
                        >
                            Create Region
                        </Button>
                    }
                </Grid>
            </SubCard>
            {/*----------------------------|| TABLE ||--------------------------------*/}
            <MainCard sx={{ marginTop: '15px' }}>
                {(errorMessage) && <ErrorAlert type={'error'} content={errorMessage} />}
                {regionEditErrorMessage && <ErrorAlert type={'error'} content={`Update Region Error : ${regionEditErrorMessage}`} />}
                <TableContainer sx={{ whiteSpace: "nowrap", maxHeight: `calc(${tableHeight}px - 380px)`, overflow: "auto" }}>
                    <Table size="small" stickyHeader aria-label="sticky table"
                        sx={{
                            '& tr > *:last-child': {
                                position: 'sticky',
                                right: 0,
                                bgcolor: 'white',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('RegionName')}
                                        endIcon={sortOrder === 'RegionName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Region Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('IsActive')}
                                        endIcon={sortOrder === 'IsActive' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Active ?
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('CreatedBy')}
                                        endIcon={sortOrder === 'CreatedBy' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Created By
                                    </Button>
                                </TableCell>
                                {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                                    <TableCell>
                                        Action
                                    </TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(regionList?.length > 0) && !isPending ?
                                regionList?.map((data, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{isEdit === data.Id ?
                                            <TextField
                                                autoFocus
                                                autoComplete="off"
                                                size="small"
                                                placeholder="Enter region name"
                                                name="regionName"
                                                onChange={validateOnchange}
                                                onKeyDown={handleKeyDown}
                                                onPaste={AllowedSpecialCharactersPaste}
                                                value={updatedRegion?.regionName || ""}
                                                error={errors?.regionName}
                                                helperText={errors?.regionName}
                                            />
                                            : (data.RegionName || 'N/A')
                                        }</TableCell>
                                        <TableCell>
                                            {isEdit === data.Id ?
                                                <Checkbox
                                                    name="isActive"
                                                    onChange={validateOnchange}
                                                    checked={updatedRegion?.isActive}
                                                    onKeyDown={handleKeyDown}
                                                />
                                                : (data.IsActive ?
                                                    <Chip label={"Active"} chipcolor="success" size="small" />
                                                    : <Chip label={"Inactive"} chipcolor="error" size="small" />
                                                )}
                                        </TableCell>
                                        <TableCell>{data.CreatedBy || 'N/A'}<br />
                                            <small>{data.CreatedTimeStamp ? moment(data.CreatedTimeStamp).format("DD MMM YYYY") : "N/A"}</small>
                                        </TableCell>
                                        {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                                            <TableCell>
                                                {isEdit === data.Id ?
                                                    isRegionEditPending ?
                                                        <Grid container alignItems={"center"} gap={1}>
                                                            <CircularProgress size={20} />
                                                            <Typography variant="h6" color={'primary'}> Loading...</Typography>
                                                        </Grid>
                                                        :
                                                        <Grid container columnGap={2}>
                                                            <Tooltip title="Submit" placement="top">
                                                                <IconCheck
                                                                    style={{ cursor: "pointer", color: "green" }}
                                                                    onClick={handleUpdateRegion}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip title="Cancel" placement="top">
                                                                <IconX
                                                                    style={{ cursor: "pointer", color: "red" }}
                                                                    onClick={() => {
                                                                        setIsEdit();
                                                                        setUpdatedRegion();
                                                                        setErrors();
                                                                        props.regionEditErrorClear();
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </Grid>
                                                    : <Tooltip title="Edit Region" placement="top">
                                                        <IconEdit style={{ cursor: "pointer" }} onClick={() => {
                                                            setIsEdit(data.Id)
                                                            setErrors();
                                                            setUpdatedRegion({
                                                                regionName: data.RegionName,
                                                                isActive: data.IsActive,
                                                                id: data.Id
                                                            });
                                                        }} />
                                                    </Tooltip>
                                                }
                                            </TableCell>}
                                    </TableRow>
                                ))
                                /*--------|| LOADING ||---------*/
                                : isPending ?
                                    <TableRow >
                                        <TableCell colSpan={8} height='300px'>
                                            <Grid container alignItems={"center"} justifyContent={"center"} gap={1}
                                                sx={{
                                                    position: "sticky",
                                                    width: "fit-content",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)"
                                                }}
                                            >
                                                <CircularProgress />
                                                <Typography variant="h5" color={'primary'}> Loading...</Typography>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                    /*--------|| NO DATA ||---------*/
                                    : <TableCell colSpan={8} height='300px'>
                                        <NoData />
                                    </TableCell>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {/*--------|| PAGINATION ||---------*/}
                <Grid>
                    <VWPagination
                        pageDetails={Pager}
                        pageChange={setPage}
                        totalRecords={totalCounts}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </MainCard>
            <FilterRegion
                open={isRegionFilterDialogOpen}
                onClose={handleFilterClose}
                setPage={setPage}
            />
            <CreateRegion
                filter={regionFilterData}
                paginationData={{
                    page: (Pager && Pager.currentPage) || 1,
                    pageSize: pageSize,
                    sortDirection: sortDirection,
                    sortOrder: sortOrder
                }}
                open={isRegionCreateDialogOpen}
                onClose={handleRegionCreateClose}
            />
        </>
    )
}

const mapStateToProps = (state) => ({
    //--------------------List--------------------------------------
    regionList: state.regions.regionList,
    totalCounts: state.regions.regionListCount,
    isPending: state.regions.isRegionListPending,
    errorMessage: state.regions.regionListErrorMessage,
    //--------------------Filter------------------------------------
    isRegionFilterDialogOpen: state.regions.isRegionFilterDialogOpen,
    regionFilterData: state.regions.regionFilterData,
    //--------------------Create------------------------------------
    isRegionCreateDialogOpen: state.regions.isRegionCreateDialogOpen,
    //--------------------Edit--------------------------------------
    regionEditErrorMessage: state.regions.regionEditErrorMessage,
    isRegionEditPending: state.regions.isRegionEditPending,
    //--------------------Profile-----------------------------------
    userProfile: state.login.userProfile,
})

const mapDispatchToProps = (dispatch) => ({
    //--------------------List--------------------------------------
    getRegionList: (page, pageSize, sortOrder, sortDirection, filter) => dispatch(getRegionList(page, pageSize, sortOrder, sortDirection, filter)),
    clearRegionList: () => dispatch(clearRegionList()),
    regionListErrorClear: () => dispatch(regionListErrorClear()),
    //--------------------Filter------------------------------------
    openRegionFilterDialog: () => dispatch(openRegionFilterDialog()),
    closeRegionFilterDialog: () => dispatch(closeRegionFilterDialog()),
    setRegionFilteredData: (filter) => dispatch(setRegionFilteredData(filter)),
    //--------------------Create------------------------------------
    openRegionCreateDialog: () => dispatch(openRegionCreateDialog()),
    closeRegionCreateDialog: () => dispatch(closeRegionCreateDialog()),
    //--------------------Edit--------------------------------------
    regionEdit: (formData, page, pageSize, sortOrder, sortDirection, filter) => dispatch(regionEdit(formData, page, pageSize, sortOrder, sortDirection, filter)),
    regionEditErrorClear: () => dispatch(regionEditErrorClear()),
})
export default connect(mapStateToProps, mapDispatchToProps)(RegionList);